import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Cpu, Sprout, Layers, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/lib/motion";
import { PRODUCTS } from "@/data/content";
import { useEnquiry } from "@/context/EnquiryContext";

const Group = ({ icon: Icon, title, items }) => (
  <div className="rounded-2xl border border-brand-ink/10 bg-white p-6">
    <div className="flex items-center gap-2.5 mb-4">
      <span className="grid place-items-center size-9 rounded-lg bg-brand-green/10 text-brand-green"><Icon className="size-5" /></span>
      <h3 className="font-heading font-bold text-lg text-brand-ink">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-sm text-brand-ink/75">
          <Check className="size-4 mt-0.5 shrink-0 text-brand-green" /> {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openEnquiry } = useEnquiry();
  const product = PRODUCTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!product) {
    return (
      <main className="min-h-screen grid place-items-center pt-24 bg-brand-cream" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-brand-ink">Product not found</h1>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-brand-green text-white">Back home</Button>
        </div>
      </main>
    );
  }

  const others = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main data-testid="product-detail" className="bg-brand-cream pt-28 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/#products" className="inline-flex items-center gap-2 text-sm text-brand-ink/60 hover:text-brand-green transition-colors mb-8">
          <ArrowLeft className="size-4" /> All Products
        </Link>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14 items-start">
          <Reveal>
            <div className="relative spotlight rounded-2xl overflow-hidden border border-brand-ink/10">
              <span className="absolute top-5 left-5 z-10 font-mono text-sm font-bold text-brand-green bg-brand-cream/90 px-3 py-1 rounded-full">{product.index}</span>
              <motion.img
                src={product.image}
                alt={product.name}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-[380px] lg:h-[520px] object-cover clip-frame"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-brown mb-3">Premium Farm Implement</p>
            <h1 className="font-heading font-extrabold text-brand-ink tracking-tighter text-3xl sm:text-5xl leading-[1.02]">{product.name}</h1>
            <p className="mt-5 text-brand-ink/75 leading-relaxed text-lg">{product.overview}</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue/10 text-brand-blue px-4 py-2 font-mono text-sm">
              <Cpu className="size-4" /> Recommended tractor: {product.hp}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="detail-enquire" onClick={() => openEnquiry(product.name, "enquiry")} className="rounded-full bg-brand-green text-white hover:bg-brand-greenDark h-12 px-8">Enquire Now</Button>
              <Button variant="outline" onClick={() => openEnquiry(product.name, "enquiry")} className="rounded-full border-brand-ink/20 h-12 px-8 hover:bg-brand-ink hover:text-white">Request Demonstration</Button>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          <Reveal><Group icon={Layers} title="Features" items={product.features} /></Reveal>
          <Reveal delay={0.08}><Group icon={Sprout} title="Benefits" items={product.benefits} /></Reveal>
          <Reveal delay={0.16}><Group icon={Check} title="Applications" items={product.applications} /></Reveal>
        </div>

        <div className="mt-20">
          <h2 className="font-heading font-bold text-2xl text-brand-ink tracking-tight mb-8">Explore more machinery</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {others.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`related-${p.slug}`} className="group rounded-2xl border border-brand-ink/10 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow]">
                <img src={p.image} alt={p.name} className="h-40 w-full object-cover clip-frame group-hover:scale-105 transition-transform duration-500" />
                <div className="p-5 flex items-center justify-between gap-3">
                  <h3 className="font-heading font-bold text-brand-ink leading-tight">{p.name}</h3>
                  <ArrowUpRight className="size-4 text-brand-green shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
